/**
 * KONFIGURACJA PARALLAX TŁA — półprzezroczysta warstwa dryfująca nad ziemią.
 *
 * Trzeci (po `ambientFx` i `atmosphere`) system wizualny per-biom „ożywiający"
 * planszę. Cel: głębia — świat ma nie być płaski.
 *
 * **Dlaczego tak, a nie „dalekie góry":** widok z góry, ziemia (`bg_*_tile`)
 * zakrywa cały świat nieprzezroczyście (depth −100), kamera ograniczona do
 * granic świata. Nie ma horyzontu. Dlatego parallax to warstwa NAD ziemią
 * (cienie przepływających chmur), przewijająca się wolniej niż gracz.
 *
 * **Technika (patrz `ParallaxBackgroundSystem`):** każda warstwa to camera-fixed
 * `TileSprite` (`scrollFactor=0`) na rozmiar viewportu. Co klatkę:
 *   `tilePositionX = cam.scrollX * parallaxFactor + elapsed * driftX`
 * `parallaxFactor` < 1 → warstwa „zostaje w tyle" za światem (głębia).
 * `driftX/Y` → powolny autonomiczny ruch (chmury suną nawet gdy gracz stoi).
 * TileSprite + tilePosition = brak szpar na krawędziach, niezależnie od resize
 * i rozmiaru świata.
 *
 * **Depth:** warstwy lądują MIĘDZY ziemią (−100) a dekoracjami (−50), np. −95,
 * żeby nie zasłaniać gameplayu (pety/gracz/destruktible są wyżej).
 *
 * **Barwa:** tekstury bake'owane docelowo; `tint` opcjonalny jak w atmosferze.
 * `blendMode` MULTIPLY = cienie (przyciemnia), NORMAL/SCREEN = jasna mgiełka.
 *
 * **Performance:** garstka TileSprite'ów + kilka setów tilePosition na klatkę.
 * Pomijalny koszt (mniej niż particle system).
 */

import type { ParallaxKey } from '@/domain/types/parallax';
export type { ParallaxKey };

/** Blend mode jako string — konwertowane na `Phaser.BlendModes` w systemie. */
export type ParallaxBlendMode = 'NORMAL' | 'ADD' | 'SCREEN' | 'MULTIPLY';

/** Pojedyncza warstwa parallax (jeden tileowalny TileSprite). */
export interface ParallaxLayerConfig {
  /** Klucz tekstury (tileowalny PNG w folderze `environment/`). Musi być
   *  załadowany w PreloadScene; brak → warstwa pominięta (fail-soft). */
  textureKey: string;
  /**
   * Jak mocno warstwa „nadąża" za scrollem świata (0–1). 1 = porusza się jak
   * świat (brak parallaxu), 0 = stoi w miejscu (najdalej). Typowo 0.3–0.6.
   */
  parallaxFactor: number;
  /** Autonomiczny dryf poziomy (px/s) — warstwa suje nawet gdy gracz stoi. */
  driftX: number;
  /** Autonomiczny dryf pionowy (px/s). */
  driftY: number;
  /** Krycie warstwy (0–1). Niska wartość = subtelnie, nie zasłania gameplayu. */
  alpha: number;
  /** Z-ordering. Ziemia=−100, deco=−50 → parallax typowo −95 (nad ziemią,
   *  pod dekoracjami), żeby nie przykrywał obiektów gry. */
  depth: number;
  /** Skala kafla tekstury (>1 = większe chmury, mniej powtórzeń). Domyślnie 1. */
  tileScale?: number;
  /** Opcjonalny tint (white × tint). Brak = oryginalne kolory tekstury. */
  tint?: number;
  /** Blend — MULTIPLY = cienie (przyciemnia), NORMAL/SCREEN = jasna mgiełka. */
  blendMode?: ParallaxBlendMode;
}

/** Pełen preset parallax — warstwy od najdalszej (back) do najbliższej (front). */
export interface ParallaxPreset {
  layers: ParallaxLayerConfig[];
}

// ─────────────────────────────────────────────────────────────────────────────

export const PARALLAX_PRESETS: Record<ParallaxKey, ParallaxPreset> = {
  // ─── MEADOW — cienie przepływających chmur ──────────────────────────────────
  // Prototyp na jednym biomie. Jedna warstwa miękkich cieni chmur sunących
  // powoli w prawo + lekko w dół, przewijająca się wolniej niż świat (0.45).
  // MULTIPLY + niska alpha = subtelne przyciemnienie ziemi „jakby chmura
  // przepłynęła", bez zasłaniania petów/gracza (depth −95, pod deco −50).
  meadow_clouds: {
    layers: [
      {
        textureKey: 'bg_meadow_parallax',
        // Jak mocno warstwa nadąża za scrollem świata. Niski = chmury „lepią się"
        // do postaci (gonią gracza); wysoki (bliżej 1) = chmury prawie jak świat,
        // gracz je mija. 0.85 = lekka głębia, ale chmury zostają w świecie. (tuning)
        parallaxFactor: 0.7,
        driftX: 4,
        driftY: 1.5,
        // alpha może być wyższa, bo tekstura ma BIAŁE tło + szare chmury, a przy
        // MULTIPLY biały=brak efektu (no-op). Cieniują tylko szare plamy chmur.
        // 0.55 = wyraźniejszy cień bez zmętnienia reszty trawy. (tuning)
        alpha: 0.55,
        depth: -95,
        // <1 zmniejsza chmury (tekstura 2048px repeatuje się częściej). 1.5 dawało
        // 1 chmurę większą niż ekran; 0.45 → kilka mniejszych chmur w kadrze. (tuning)
        tileScale: 0.45,
        blendMode: 'MULTIPLY',   // biały no-opuje → nie trzeba alpha-channel w PNG
      },
    ],
  },

  // ─── MOUNTAIN — mroźna mgła / śnieżna poświata ─────────────────────────────
  // Jasne, chłodne pasma mgły na czarnym tle. SCREEN sprawia, że czarny jest
  // no-opem, a tylko niebiesko-białe smugi rozjaśniają kamień i śnieg. Alpha
  // celowo wyższa niż w prototypie meadow, a tileScale większy, żeby mgły były
  // widoczne, ale nie powtarzały się zbyt gęsto obok siebie.
  mountain_mist: {
    layers: [
      {
        textureKey: 'bg_mountain_parallax',
        parallaxFactor: 0.62,
        driftX: 2.2,
        driftY: -1.2,
        alpha: 0.48,
        depth: -95,
        tileScale: 0.85,
        blendMode: 'SCREEN',  // czarny no-opuje, jasna mgła delikatnie rozświetla
      },
    ],
  },

  // ─── STARWARS/TATOOINE — tumany pyłu nad piaskiem ──────────────────────────
  // Ciepłe szaro-piaskowe smugi na białym tle. MULTIPLY zachowuje biały jako
  // brak efektu, a pasma pyłu lekko przyciemniają pustynny ground tile. Drift
  // idzie głównie poziomo, żeby pasował do ambientowego wiatru piaskowego.
  starwars_dust: {
    layers: [
      {
        textureKey: 'bg_starwars_parallax',
        parallaxFactor: 0.78,
        driftX: -8,
        driftY: 2,
        alpha: 0.42,
        depth: -95,
        tileScale: 0.55,
        blendMode: 'MULTIPLY', // biały no-opuje, szaro-piaskowe pasma cieniują
      },
    ],
  },

  // ─── PIXEL WORLD — neonowy retro overlay ───────────────────────────────────
  // Kolorowe pixelowe linie/glitche na czarnym tle. ADD sprawia, że czarny jest
  // no-opem, a neonowe elementy dają subtelny arcade shimmer. Tekstura ma
  // 'pixel' w kluczu, więc PreloadScene ustawi jej NEAREST filter.
  pixel_neon: {
    layers: [
      {
        textureKey: 'bg_pixel_parallax',
        parallaxFactor: 0.55,
        driftX: 5,
        driftY: -3,
        alpha: 0.22,
        depth: -95,
        tileScale: 1,
        blendMode: 'ADD',     // czarny no-opuje, neonowe pixele świecą subtelnie
      },
    ],
  },

  // ─── OCEAN — ruchoma sieć kaustyk (światło załamane przez fale) ─────────────
  // Cyjanowo-białe wijące się wstęgi światła na czarnym tle. SCREEN sprawia, że
  // czarny jest no-opem, a jasna sieć kaustyk delikatnie pełza po dnie morskim —
  // klasyczny podwodny efekt „tańczącego światła". Dryf wolny, w obu osiach,
  // żeby sieć falowała leniwie nad piaskiem.
  ocean_caustics: {
    layers: [
      {
        textureKey: 'bg_ocean_parallax',
        parallaxFactor: 0.6,
        driftX: 5,
        driftY: 3,
        alpha: 0.34,
        depth: -95,
        tileScale: 0.7,
        blendMode: 'SCREEN',  // czarny no-opuje, jasne kaustyki rozświetlają dno
      },
    ],
  },

  // ─── FOOD — unoszące się smugi pary / aromatu znad gorącego jedzenia ────────
  // Miękkie kremowo-białe wstęgi pary na czarnym tle. SCREEN sprawia, że czarny
  // jest no-opem, a jasne smugi delikatnie dryfują nad blatem jak zapach unoszący
  // się znad frytek i burgerów. Dryf głównie ku górze (para idzie w górę), powolny.
  food_steam: {
    layers: [
      {
        textureKey: 'bg_food_parallax',
        parallaxFactor: 0.6,
        driftX: 3,
        driftY: -5,
        alpha: 0.38,
        depth: -95,
        tileScale: 0.7,
        blendMode: 'SCREEN',  // czarny no-opuje, jasna para delikatnie się unosi
      },
    ],
  },

  // ── ATLANTIS (biom 7) — dryfujące złote smugi boskiego światła (SCREEN) ──
  atlantis_godrays: {
    layers: [
      {
        textureKey: 'bg_atlantis_parallax',
        parallaxFactor: 0.5,
        driftX: 4,
        driftY: 6,
        alpha: 0.4,
        depth: -95,
        tileScale: 0.8,
        blendMode: 'SCREEN',  // czarny no-opuje, złote smugi delikatnie dryfują
      },
    ],
  },

  // ─── BEACH (event Wakacje Capibar) — leniwe chmurki + blask słońca ──────────
  // Tekstura na czarnym tle (SCREEN no-opuje czerń), dryfuje wolno nad plażą.
  beach_clouds: {
    layers: [
      {
        textureKey: 'bg_capibara_beach_parallax',
        parallaxFactor: 0.5,
        driftX: 6,
        driftY: 2,
        alpha: 0.45,
        depth: -95,
        tileScale: 0.9,
        blendMode: 'SCREEN',
      },
    ],
  },

  // ─── SCHOOL (biom 8) — plamy światła z okien pełzające po podłodze ─────────
  // Top-down: to NIE chmury (te kładłyby się dziwnie na kartce), tylko prostokątne
  // plamy słońca z wysokich okien klasy, wędrujące wolno jak w ciągu lekcji.
  // Tekstura na CZARNYM tle — SCREEN no-opuje czerń.
  school_sunbeams: {
    layers: [
      {
        textureKey: 'bg_school_parallax',
        // Nad groundMarks (-90) i papierowymi matami (-89), ale nadal pod
        // rekwizytami. Wcześniejsze -95 chowało światło ZA centralną kartką.
        parallaxFactor: 0.32,
        driftX: 2.2,
        driftY: 1.2,
        alpha: 0.42,
        depth: -86,
        tileScale: 1.25,
        blendMode: 'SCREEN',
      },
      {
        // Osobna warstwa ram okien. MULTIPLY zachowuje ciemne poprzeczki,
        // których SCREEN z jasnej warstwy praktycznie nie pokazywał.
        textureKey: 'bg_school_window_shadows_parallax',
        parallaxFactor: 0.22,
        driftX: 1.6,
        driftY: 0.8,
        alpha: 0.22,
        depth: -85,
        tileScale: 0.85,
        blendMode: 'MULTIPLY',
      },
    ],
  },

  // ─── ARCTIC (biom 9) — zorza na SCREEN + miękkie cienie na MULTIPLY ──────
  // Dwie niezależne, seamless warstwy dają rytm światło/cień nad ciągłą krą.
  arctic_aurora: {
    layers: [
      {
        textureKey: 'bg_arctic_aurora_parallax',
        parallaxFactor: 0.3,
        driftX: 2.4,
        driftY: -0.8,
        alpha: 0.4,
        depth: -86,
        tileScale: 1.2,
        blendMode: 'SCREEN',
      },
      {
        textureKey: 'bg_arctic_cloud_shadows_parallax',
        parallaxFactor: 0.56,
        driftX: 4.2,
        driftY: 1.3,
        alpha: 0.16,
        depth: -85,
        tileScale: 0.8,
        blendMode: 'MULTIPLY',
      },
    ],
  },
};
